(() => {
  const desktopMedia = window.matchMedia('(min-width: 990px)');
  const cache = new Map();

  let dialog;
  let content;
  let status;
  let activeTrigger = null;
  let activeRequest = null;
  let openToken = 0;

  const getDialogParts = () => {
    dialog ||= document.querySelector('[data-luvia-product-qv-modal]');
    content ||= dialog?.querySelector('[data-luvia-product-qv-content]');
    status ||= dialog?.querySelector('[data-luvia-product-qv-status]');
    return Boolean(dialog && content && status);
  };

  const lockPage = () => {
    document.documentElement.classList.add('luvia-product-qv-open');
    document.body.classList.add('luvia-product-qv-open');
  };

  const unlockPage = () => {
    document.documentElement.classList.remove('luvia-product-qv-open');
    document.body.classList.remove('luvia-product-qv-open');
  };

  const setStatus = (message, state = 'loading') => {
    if (!status || !content) return;
    status.querySelectorAll('.luvia-product-qv__fallback-link').forEach((link) => link.remove());
    status.dataset.state = state;
    const label = status.querySelector('span:last-child');
    if (label) label.textContent = message;
    status.hidden = false;
    content.hidden = true;
  };

  const showContent = () => {
    if (!status || !content) return;
    status.hidden = true;
    content.hidden = false;
  };

  const closeDialog = (restoreFocus = true) => {
    if (!getDialogParts()) return;

    activeRequest?.abort();
    activeRequest = null;

    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    activeTrigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) activeTrigger?.focus({ preventScroll: true });
    activeTrigger = null;
    unlockPage();
  };

  const openNativeDialog = () => {
    lockPage();
    try {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } catch (error) {
      dialog.setAttribute('open', '');
    }
  };

  const copyPreviewContext = (url) => {
    const currentParams = new URLSearchParams(window.location.search);
    const previewKeys = ['preview_theme_id', '_fd', 'pb', 'previewPath'];

    previewKeys.forEach((key) => {
      const value = currentParams.get(key);
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    });

    return url;
  };

  const buildQuickViewUrl = (productUrl) => {
    const url = copyPreviewContext(new URL(productUrl, window.location.origin));
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.set('view', 'quick-view');
    return url.toString();
  };

  const fetchText = async (url, signal) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      signal
    });

    if (!response.ok) throw new Error(`Quick view request failed: ${response.status}`);
    return response.text();
  };

  const findProductSection = (html) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector('.product-template[data-product-section]')
      || parsed.querySelector('[data-product-section]');
  };

  const loadProductSectionHtml = async (productUrl, signal) => {
    const requestUrl = buildQuickViewUrl(productUrl);
    const request = new URL(requestUrl);
    const cacheKey = `${request.pathname}?${request.searchParams.toString()}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const html = await fetchText(requestUrl, signal);
    const section = findProductSection(html);

    if (!section) {
      throw new Error(`Quick-view product template did not return the product section: ${requestUrl}`);
    }

    const sectionHtml = section.outerHTML;
    cache.set(cacheKey, sectionHtml);
    return sectionHtml;
  };

  const makeIdsUnique = (root) => {
    const prefix = `LuviaProductQv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-`;
    const idMap = new Map();

    root.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      const newId = `${prefix}${oldId}`;
      idMap.set(oldId, newId);
      element.id = newId;
    });

    const referenceAttributes = ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'list'];
    root.querySelectorAll('*').forEach((element) => {
      referenceAttributes.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const mapped = value
          .split(/\s+/)
          .map((token) => idMap.get(token) || token)
          .join(' ');
        element.setAttribute(attribute, mapped);
      });

      const href = element.getAttribute('href');
      if (href?.startsWith('#')) {
        const oldTarget = href.slice(1);
        if (idMap.has(oldTarget)) element.setAttribute('href', `#${idMap.get(oldTarget)}`);
      }
    });
  };

  const prepareProductSection = (section, productUrl) => {
    section.classList.add('luvia-product-qv__product-page');
    section.dataset.quickViewProductPage = 'true';

    section.querySelector('.breadcrumbs')?.remove();
    section.querySelector('[data-sticky-product-bar]')?.remove();
    section.querySelector('.product-recommendations-luvia')?.remove();

    section.querySelectorAll('[data-main-product-form]').forEach((form) => {
      form.removeAttribute('data-main-product-form');
    });

    section.querySelectorAll('variant-radios').forEach((picker) => {
      picker.removeAttribute('data-url');
    });

    const heading = section.querySelector('.product-info h1');
    if (heading) heading.id = 'LuviaProductQuickViewTitle';

    section.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      const url = new URL(href, window.location.origin);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname) {
        link.href = new URL(productUrl, window.location.origin).toString();
      }
    });

    makeIdsUnique(section);

    const uniqueHeading = section.querySelector('.product-info h1');
    if (uniqueHeading) {
      uniqueHeading.id = 'LuviaProductQuickViewTitle';
      dialog.setAttribute('aria-labelledby', uniqueHeading.id);
    }

    return section;
  };

  const parseGalleryVariants = (gallery) => {
    const script = gallery.querySelector('[data-gallery-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      return [];
    }
  };

  const initializeGallery = (gallery) => {
    if (!gallery || gallery.dataset.luviaProductQvGalleryReady === 'true') return;
    gallery.dataset.luviaProductQvGalleryReady = 'true';

    const items = Array.from(gallery.querySelectorAll('[data-gallery-item]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    if (!items.length) return;

    const activate = (index) => {
      const normalized = Math.max(0, Math.min(Number(index) || 0, items.length - 1));
      items.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === normalized));
      thumbs.forEach((thumb) => {
        const thumbIndex = Number(thumb.dataset.mediaIndex || 0);
        const isMore = thumb.hasAttribute('data-gallery-more');
        const active = isMore ? normalized >= thumbIndex : normalized === thumbIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      gallery.dataset.activeMediaIndex = String(normalized);
    };

    const initial = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    activate(initial);

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => activate(Number(thumb.dataset.mediaIndex || 0)));
    });

    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => {
      const current = Number(gallery.dataset.activeMediaIndex || 0);
      activate((current - 1 + items.length) % items.length);
    });

    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
      const current = Number(gallery.dataset.activeMediaIndex || 0);
      activate((current + 1) % items.length);
    });

    gallery._luviaProductQvActivate = activate;
    gallery._luviaProductQvVariants = parseGalleryVariants(gallery);
  };

  const initializeQuantity = (section) => {
    section.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach((button) => {
      if (button.dataset.luviaProductQvQtyReady === 'true') return;
      button.dataset.luviaProductQvQtyReady = 'true';

      button.addEventListener('click', () => {
        const input = button.parentElement?.querySelector('input[type="number"]');
        if (!input) return;
        const minimum = Number(input.min || 1);
        const current = Number(input.value || minimum);
        input.value = button.hasAttribute('data-qty-plus')
          ? current + 1
          : Math.max(minimum, current - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const updateCartCount = async () => {
    const cartResponse = await fetch(`${window.themeRoutes.cartUrl}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!cartResponse.ok) throw new Error('Cart refresh failed');

    const cart = await cartResponse.json();
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
    });
  };

  const showCartToast = () => {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.hidden = false;
    window.setTimeout(() => {
      toast.hidden = true;
    }, 3600);
  };

  const initializeProductForms = (section) => {
    section.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.luviaProductQvFormReady === 'true') return;
      form.dataset.luviaProductQvFormReady = 'true';

      form.addEventListener('submit', async (event) => {
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();

        const submit = form.querySelector('button[type="submit"]');
        const previousText = submit?.textContent?.trim() || 'Añadir al carrito';

        if (submit) {
          submit.disabled = true;
          submit.setAttribute('aria-busy', 'true');
          submit.textContent = 'Añadiendo…';
        }

        try {
          const response = await fetch(window.themeRoutes.cartAdd, {
            method: 'POST',
            body: new FormData(form),
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });

          if (!response.ok) throw new Error('Cart add failed');
          await updateCartCount();
          showCartToast();
        } catch (error) {
          HTMLFormElement.prototype.submit.call(form);
        } finally {
          if (submit) {
            const available = submit.dataset.variantAvailable !== 'false';
            submit.disabled = !available;
            submit.removeAttribute('aria-busy');
            submit.textContent = available ? previousText : 'Agotado';
          }
        }
      });
    });
  };

  const initializeDescriptionLinks = (section) => {
    section.querySelectorAll('[data-product-description-link]').forEach((link) => {
      if (link.dataset.luviaProductQvDescriptionReady === 'true') return;
      link.dataset.luviaProductQvDescriptionReady = 'true';

      link.addEventListener('click', (event) => {
        const selector = link.getAttribute('href');
        const target = selector?.startsWith('#') ? section.querySelector(selector) : null;
        if (!target) return;
        event.preventDefault();
        target.open = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const initializeLoadedProduct = (section) => {
    section.querySelectorAll('[data-product-gallery]').forEach(initializeGallery);
    initializeQuantity(section);
    initializeProductForms(section);
    initializeDescriptionLinks(section);

    if (window.customElements?.upgrade) window.customElements.upgrade(section);
    if (typeof window.updateDispatchDates === 'function') window.updateDispatchDates();
  };

  const renderProduct = async (productUrl, token) => {
    activeRequest?.abort();
    const controller = new AbortController();
    activeRequest = controller;
    setStatus('Cargando producto…', 'loading');

    try {
      const html = await loadProductSectionHtml(productUrl, controller.signal);
      if (token !== openToken) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const rawSection = parsed.querySelector('.product-template[data-product-section]')
        || parsed.querySelector('[data-product-section]');
      if (!rawSection) throw new Error('Product section was not found after parsing');

      const section = prepareProductSection(rawSection, productUrl);
      content.replaceChildren(section);
      initializeLoadedProduct(section);
      showContent();
      content.scrollTop = 0;

      window.requestAnimationFrame(() => {
        content.scrollTop = 0;
        dialog.querySelector('[data-luvia-product-qv-close]')?.focus({ preventScroll: true });
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[Luvia quick view v9.4]', error);
      setStatus('No se pudo cargar la vista rápida.', 'error');

      const link = document.createElement('a');
      link.className = 'luvia-product-qv__fallback-link';
      link.href = productUrl;
      link.textContent = 'Ver la página del producto';
      status.append(link);
    } finally {
      if (activeRequest === controller) activeRequest = null;
    }
  };

  const openProduct = (trigger) => {
    if (!desktopMedia.matches || !getDialogParts()) return;

    const productUrl = trigger.dataset.productUrl;
    if (!productUrl) return;

    activeTrigger?.setAttribute('aria-expanded', 'false');
    activeTrigger = trigger;
    activeTrigger.setAttribute('aria-expanded', 'true');

    openToken += 1;
    openNativeDialog();
    renderProduct(productUrl, openToken);
  };

  const bindDialog = () => {
    if (!getDialogParts() || dialog.dataset.luviaProductQvBound === 'true') return;
    dialog.dataset.luviaProductQvBound = 'true';

    dialog.querySelector('[data-luvia-product-qv-close]')?.addEventListener('click', () => closeDialog());

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });

    dialog.addEventListener('close', () => {
      activeTrigger?.setAttribute('aria-expanded', 'false');
      unlockPage();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-luvia-product-qv]');
    if (!trigger) return;
    event.preventDefault();
    openProduct(trigger);
  });

  document.addEventListener('luvia:variant-change', (event) => {
    const section = event.detail?.section;
    const variant = event.detail?.variant;
    if (!section?.dataset.quickViewProductPage || !variant) return;

    const gallery = section.querySelector('[data-product-gallery]');
    const items = Array.from(gallery?.querySelectorAll('[data-gallery-item]') || []);
    if (!gallery || !items.length) return;

    const mediaId = variant.featured_media?.id || variant.media_id;
    const imageId = variant.featured_image?.id || variant.image?.id || variant.image_id;
    let targetIndex = -1;

    if (mediaId) targetIndex = items.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
    if (targetIndex < 0 && imageId) targetIndex = items.findIndex((item) => String(item.dataset.imageId) === String(imageId));
    if (targetIndex >= 0) gallery._luviaProductQvActivate?.(targetIndex);
  });

  desktopMedia.addEventListener?.('change', (event) => {
    if (!event.matches && dialog?.open) closeDialog(false);
  });

  document.addEventListener('DOMContentLoaded', bindDialog);
  document.addEventListener('shopify:section:load', bindDialog);
})();
